import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Slider } from "../components/ui/slider";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Download,
  Save,
  Sparkles,
  User,
  Building2,
  Phone,
  Mail,
  Globe,
  MapPin,
  Linkedin,
  Twitter,
  Instagram,
  Image as ImageIcon,
  Loader2,
  Palette,
  Type,
  LayoutGrid,
  Square,
  Circle,
  Sun,
  Layers,
  AlignLeft,
  AlignCenter,
  AlignRight,
} from "lucide-react";
import { storage } from "../lib/storage";
import templatesData from "../lib/templates.json";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

// Preset color palettes
const COLOR_PRESETS = [
  { name: "Classic", bg: "#ffffff", text: "#0a0a0a", accent: "#000000" },
  { name: "Midnight", bg: "#0f172a", text: "#f8fafc", accent: "#38bdf8" },
  { name: "Forest", bg: "#f0fdf4", text: "#14532d", accent: "#22c55e" },
  { name: "Sunset", bg: "#fef3c7", text: "#78350f", accent: "#f59e0b" },
  { name: "Ocean", bg: "#ecfeff", text: "#164e63", accent: "#06b6d4" },
  { name: "Rose", bg: "#fff1f2", text: "#881337", accent: "#f43f5e" },
  { name: "Lavender", bg: "#faf5ff", text: "#581c87", accent: "#a855f7" },
  { name: "Slate", bg: "#f8fafc", text: "#1e293b", accent: "#64748b" },
];

// Font options
const FONT_OPTIONS = [
  { name: "Modern", value: "Manrope, sans-serif" },
  { name: "Elegant", value: "Playfair Display, serif" },
  { name: "Tech", value: "JetBrains Mono, monospace" },
  { name: "Classic", value: "Georgia, serif" },
  { name: "Clean", value: "Inter, sans-serif" },
  { name: "Bold", value: "Space Grotesk, sans-serif" },
];

// Layout options
const LAYOUT_OPTIONS = [
  { name: "Standard", value: "standard" },
  { name: "Centered", value: "centered" },
  { name: "Minimal", value: "minimal" },
  { name: "Bold", value: "bold" },
];

const Editor = () => {
  const navigate = useNavigate();
  const { cardId } = useParams();
  const [searchParams] = useSearchParams();
  const templateIdParam = searchParams.get("template");
  const cardRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isGettingSuggestions, setIsGettingSuggestions] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [activeTab, setActiveTab] = useState("details");

  const [template, setTemplate] = useState(null);
  const [cardData, setCardData] = useState({
    full_name: "",
    job_title: "",
    company_name: "",
    phone: "",
    email: "",
    website: "",
    address: "",
    logo_url: "",
    social_linkedin: "",
    social_twitter: "",
    social_instagram: "",
  });

  // Card customization state
  const [customization, setCustomization] = useState({
    backgroundColor: "#ffffff",
    textColor: "#0a0a0a",
    accentColor: "#000000",
    borderRadius: 16,
    shadowIntensity: 2, // 0-4 scale
    fontFamily: "Manrope, sans-serif",
    layout: "standard",
    textAlign: "left",
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardId, templateIdParam]);

  const loadData = () => {
    setIsLoading(true);
    try {
      if (cardId) {
        const card = storage.getCard(cardId);
        if (!card) {
          toast.error("Card not found");
          navigate("/dashboard");
          return;
        }
        setCardData(card.card_data);
        if (card.customization && Object.keys(card.customization).length > 0) {
          setCustomization(prev => ({ ...prev, ...card.customization }));
        }
        const foundTemplate = templatesData.find(t => t.id === card.template_id);
        setTemplate(foundTemplate);

        if (!card.customization?.backgroundColor && foundTemplate) {
          setCustomization(prev => ({
            ...prev,
            backgroundColor: foundTemplate.secondary_color,
            accentColor: foundTemplate.primary_color,
          }));
        }
      } else if (templateIdParam) {
        const foundTemplate = templatesData.find(t => t.id === templateIdParam);
        if (!foundTemplate) {
          navigate("/templates");
          return;
        }
        setTemplate(foundTemplate);
        const isDark = isTemplateDark(templateIdParam);
        setCustomization(prev => ({
          ...prev,
          backgroundColor: foundTemplate.secondary_color,
          textColor: isDark ? "#ffffff" : "#0a0a0a",
          accentColor: foundTemplate.primary_color,
        }));
      } else {
        navigate("/templates");
        return;
      }
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load editor");
      navigate("/templates");
    } finally {
      setIsLoading(false);
    }
  };

  const isTemplateDark = (id) => {
    const darkTemplates = [
      "minimal-black", "tech-dark", "tech-neon", "elegant-gold",
      "elegant-silver", "luxury-black", "startup-orange", "startup-teal", "finance-dark"
    ];
    return darkTemplates.includes(id);
  };

  const handleSave = () => {
    setIsSaving(true);
    try {
      const cardToSave = {
        card_id: cardId,
        template_id: template.id,
        card_data: cardData,
        customization,
      };
      const savedCard = storage.saveCard(cardToSave);
      toast.success(cardId ? "Card saved!" : "Card created!");
      if (!cardId) {
        navigate(`/editor/${savedCard.card_id}`, { replace: true });
      }
    } catch (error) {
      toast.error("Failed to save card");
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportPNG = async () => {
    if (!cardRef.current) return;
    setIsExporting(true);

    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 3,
        backgroundColor: null,
        useCORS: true,
      });

      const link = document.createElement("a");
      link.download = `${cardData.full_name || "business-card"}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("PNG downloaded!");
    } catch (error) {
      toast.error("Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPDF = async () => {
    if (!cardRef.current) return;
    setIsExporting(true);

    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 3,
        backgroundColor: null,
        useCORS: true,
      });

      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "in",
        format: [3.5, 2],
      });

      const imgData = canvas.toDataURL("image/png");
      pdf.addImage(imgData, "PNG", 0, 0, 3.5, 2);
      pdf.save(`${cardData.full_name || "business-card"}.pdf`);
      toast.success("PDF downloaded!");
    } catch (error) {
      toast.error("Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  const handleGetAISuggestions = () => {
    setIsGettingSuggestions(true);
    // Mock AI suggestions for standalone mode
    setTimeout(() => {
      setAiSuggestions({
        color_suggestions: [
          { primary: "#000000", secondary: "#ffffff", accent: "#2563EB", reason: "Classic professional palette" },
          { primary: "#1e40af", secondary: "#f8fafc", accent: "#fbbf24", reason: "Corporate trust with warmth" }
        ],
        font_suggestions: [
          { heading: "Playfair Display", body: "Manrope", reason: "Elegant and highly readable" },
          { heading: "Space Grotesk", body: "Inter", reason: "Modern tech aesthetic" }
        ],
        layout_tips: [
          "Maintain 3mm bleed area for print safety",
          "Keep text at least 5mm from edges",
          "Ensure logo is high resolution (300 DPI)"
        ],
        overall_feedback: "Great start! Try experimenting with the color presets for a different vibe."
      });
      toast.success("AI suggestions ready!");
      setIsGettingSuggestions(false);
    }, 1500);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCardData({ ...cardData, logo_url: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const applyColorPreset = (preset) => {
    setCustomization(prev => ({
      ...prev,
      backgroundColor: preset.bg,
      textColor: preset.text,
      accentColor: preset.accent,
    }));
  };

  const getShadowClass = () => {
    const shadows = [
      "card-preview-shadow-none",
      "card-preview-shadow-soft",
      "card-preview-shadow-medium",
      "card-preview-shadow-strong",
      "card-preview-shadow-float",
    ];
    return shadows[customization.shadowIntensity] || shadows[2];
  };

  const getTextColorWithOpacity = (opacity = 1) => {
    const hex = customization.textColor;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading editor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden">
      {/* Header */}
      <header className="shrink-0 border-b border-border bg-white">
        <div className="h-16 px-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon" className="rounded-full hover-warm">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="font-semibold">
                {cardData.company_name || "Untitled Card"}
              </h1>
              <p className="text-xs text-muted-foreground">{template?.name}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="rounded-full hover-warm"
              onClick={handleSave}
              disabled={isSaving}
              data-testid="save-card-btn"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save
            </Button>
            <Button
              variant="outline"
              className="rounded-full hover-warm"
              onClick={handleExportPNG}
              disabled={isExporting}
              data-testid="export-png-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              PNG
            </Button>
            <Button
              className="rounded-full bg-primary text-primary-foreground btn-modern"
              onClick={handleExportPDF}
              disabled={isExporting}
              data-testid="export-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              PDF
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar */}
        <aside className="w-80 border-r border-border bg-white shrink-0 flex flex-col">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
            <TabsList className="shrink-0 w-full justify-start rounded-none border-b bg-transparent p-0 h-12">
              <TabsTrigger
                value="details"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4"
              >
                <User className="w-4 h-4 mr-2" />
                Details
              </TabsTrigger>
              <TabsTrigger
                value="style"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4"
              >
                <Palette className="w-4 h-4 mr-2" />
                Style
              </TabsTrigger>
              <TabsTrigger
                value="ai"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                AI
              </TabsTrigger>
            </TabsList>

            <ScrollArea className="flex-1">
              {/* Details Tab */}
              <TabsContent value="details" className="p-4 space-y-4 mt-0">
                {/* Basic Info */}
                <div className="space-y-3">
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Basic Info</h3>

                  <div className="space-y-2">
                    <Label className="text-xs">Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="John Doe"
                        className="pl-9 h-10 rounded-xl input-glow"
                        value={cardData.full_name}
                        onChange={(e) => setCardData({ ...cardData, full_name: e.target.value })}
                        data-testid="input-full-name"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs">Job Title</Label>
                    <Input
                      placeholder="CEO & Founder"
                      className="h-10 rounded-xl input-glow"
                      value={cardData.job_title}
                      onChange={(e) => setCardData({ ...cardData, job_title: e.target.value })}
                      data-testid="input-job-title"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs">Company Name</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Acme Inc."
                        className="pl-9 h-10 rounded-xl input-glow"
                        value={cardData.company_name}
                        onChange={(e) => setCardData({ ...cardData, company_name: e.target.value })}
                        data-testid="input-company-name"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="space-y-3 pt-4 border-t">
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Contact</h3>

                  <div className="space-y-2">
                    <Label className="text-xs">Phone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="+1 (555) 123-4567"
                        className="pl-9 h-10 rounded-xl input-glow"
                        value={cardData.phone}
                        onChange={(e) => setCardData({ ...cardData, phone: e.target.value })}
                        data-testid="input-phone"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="hello@company.com"
                        className="pl-9 h-10 rounded-xl input-glow"
                        value={cardData.email}
                        onChange={(e) => setCardData({ ...cardData, email: e.target.value })}
                        data-testid="input-email"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs">Website</Label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="www.company.com"
                        className="pl-9 h-10 rounded-xl input-glow"
                        value={cardData.website}
                        onChange={(e) => setCardData({ ...cardData, website: e.target.value })}
                        data-testid="input-website"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs">Address</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="123 Main St, City"
                        className="pl-9 h-10 rounded-xl input-glow"
                        value={cardData.address}
                        onChange={(e) => setCardData({ ...cardData, address: e.target.value })}
                        data-testid="input-address"
                      />
                    </div>
                  </div>
                </div>

                {/* Logo Upload */}
                <div className="space-y-3 pt-4 border-t">
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Logo</h3>
                  <div className="flex items-center gap-3">
                    <div className="w-16 h-16 bg-secondary rounded-xl flex items-center justify-center overflow-hidden">
                      {cardData.logo_url ? (
                        <img src={cardData.logo_url} alt="Logo" className="w-full h-full object-contain" />
                      ) : (
                        <ImageIcon className="w-6 h-6 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1">
                      <input type="file" accept="image/*" className="hidden" id="logo-upload" onChange={handleLogoUpload} />
                      <label htmlFor="logo-upload">
                        <Button variant="outline" size="sm" className="rounded-xl cursor-pointer hover-warm" asChild>
                          <span data-testid="upload-logo-btn">Upload Logo</span>
                        </Button>
                      </label>
                      {cardData.logo_url && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive ml-2"
                          onClick={() => setCardData({ ...cardData, logo_url: "" })}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Social Links */}
                <div className="space-y-3 pt-4 border-t">
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Social</h3>

                  <div className="relative">
                    <Linkedin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="linkedin.com/in/username"
                      className="pl-9 h-10 rounded-xl input-glow"
                      value={cardData.social_linkedin}
                      onChange={(e) => setCardData({ ...cardData, social_linkedin: e.target.value })}
                      data-testid="input-linkedin"
                    />
                  </div>

                  <div className="relative">
                    <Twitter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="@username"
                      className="pl-9 h-10 rounded-xl input-glow"
                      value={cardData.social_twitter}
                      onChange={(e) => setCardData({ ...cardData, social_twitter: e.target.value })}
                      data-testid="input-twitter"
                    />
                  </div>

                  <div className="relative">
                    <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="@username"
                      className="pl-9 h-10 rounded-xl input-glow"
                      value={cardData.social_instagram}
                      onChange={(e) => setCardData({ ...cardData, social_instagram: e.target.value })}
                      data-testid="input-instagram"
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Style Tab */}
              <TabsContent value="style" className="p-4 space-y-5 mt-0">
                {/* Color Presets */}
                <div className="space-y-3">
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Color Presets</h3>
                  <div className="grid grid-cols-4 gap-2">
                    {COLOR_PRESETS.map((preset) => (
                      <button
                        key={preset.name}
                        className={`group relative p-1 rounded-xl transition-all duration-200 hover:scale-105 ${customization.backgroundColor === preset.bg ? 'ring-2 ring-primary ring-offset-2' : ''
                          }`}
                        onClick={() => applyColorPreset(preset)}
                        data-testid={`color-preset-${preset.name.toLowerCase()}`}
                      >
                        <div
                          className="aspect-square rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: preset.bg }}
                        >
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: preset.accent }}
                          />
                        </div>
                        <span className="text-[10px] text-muted-foreground mt-1 block text-center">
                          {preset.name}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Colors */}
                <div className="space-y-3 pt-4 border-t">
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Custom Colors</h3>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Background</Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={customization.backgroundColor}
                          onChange={(e) => setCustomization({ ...customization, backgroundColor: e.target.value })}
                          className="w-8 h-8 rounded-lg cursor-pointer border-0"
                          data-testid="color-background"
                        />
                        <span className="text-xs font-mono text-muted-foreground w-16">
                          {customization.backgroundColor}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Text</Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={customization.textColor}
                          onChange={(e) => setCustomization({ ...customization, textColor: e.target.value })}
                          className="w-8 h-8 rounded-lg cursor-pointer border-0"
                          data-testid="color-text"
                        />
                        <span className="text-xs font-mono text-muted-foreground w-16">
                          {customization.textColor}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Accent</Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={customization.accentColor}
                          onChange={(e) => setCustomization({ ...customization, accentColor: e.target.value })}
                          className="w-8 h-8 rounded-lg cursor-pointer border-0"
                          data-testid="color-accent"
                        />
                        <span className="text-xs font-mono text-muted-foreground w-16">
                          {customization.accentColor}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Border Radius */}
                <div className="space-y-3 pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Corner Radius</h3>
                    <span className="text-xs font-mono text-muted-foreground">{customization.borderRadius}px</span>
                  </div>
                  <Slider
                    value={[customization.borderRadius]}
                    onValueChange={([val]) => setCustomization({ ...customization, borderRadius: val })}
                    min={0}
                    max={32}
                    step={4}
                    className="w-full"
                    data-testid="slider-border-radius"
                  />
                  <div className="flex justify-between">
                    {[0, 8, 16, 24, 32].map((val) => (
                      <button
                        key={val}
                        onClick={() => setCustomization({ ...customization, borderRadius: val })}
                        className={`w-8 h-8 rounded border transition-all ${customization.borderRadius === val
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                          }`}
                        style={{ borderRadius: val / 2 }}
                      />
                    ))}
                  </div>
                </div>

                {/* Shadow */}
                <div className="space-y-3 pt-4 border-t">
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Shadow</h3>
                  <div className="grid grid-cols-5 gap-2">
                    {['None', 'Soft', 'Medium', 'Strong', 'Float'].map((label, idx) => (
                      <button
                        key={label}
                        onClick={() => setCustomization({ ...customization, shadowIntensity: idx })}
                        className={`p-2 rounded-xl text-xs transition-all ${customization.shadowIntensity === idx
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-secondary hover:bg-secondary/80'
                          }`}
                        data-testid={`shadow-${label.toLowerCase()}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Typography */}
                <div className="space-y-3 pt-4 border-t">
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Typography</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {FONT_OPTIONS.map((font) => (
                      <button
                        key={font.name}
                        onClick={() => setCustomization({ ...customization, fontFamily: font.value })}
                        className={`p-3 rounded-xl text-left transition-all ${customization.fontFamily === font.value
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-secondary hover:bg-secondary/80'
                          }`}
                        style={{ fontFamily: font.value }}
                        data-testid={`font-${font.name.toLowerCase()}`}
                      >
                        <span className="text-sm font-medium">{font.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Text Alignment */}
                <div className="space-y-3 pt-4 border-t">
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Text Alignment</h3>
                  <div className="flex gap-2">
                    {[
                      { value: 'left', icon: AlignLeft },
                      { value: 'center', icon: AlignCenter },
                      { value: 'right', icon: AlignRight },
                    ].map(({ value, icon: Icon }) => (
                      <button
                        key={value}
                        onClick={() => setCustomization({ ...customization, textAlign: value })}
                        className={`flex-1 p-3 rounded-xl flex items-center justify-center transition-all ${customization.textAlign === value
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-secondary hover:bg-secondary/80'
                          }`}
                        data-testid={`align-${value}`}
                      >
                        <Icon className="w-4 h-4" />
                      </button>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* AI Tab */}
              <TabsContent value="ai" className="p-4 mt-0">
                <div className="space-y-4">
                  <div className="text-center py-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Sparkles className="w-6 h-6 text-amber-600" />
                    </div>
                    <h3 className="font-semibold mb-2">AI Design Assistant</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Get smart suggestions for colors, fonts, and layout based on your card details.
                    </p>
                    <Button
                      className="rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600"
                      onClick={handleGetAISuggestions}
                      disabled={isGettingSuggestions}
                      data-testid="get-ai-suggestions-btn"
                    >
                      {isGettingSuggestions ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Sparkles className="w-4 h-4 mr-2" />
                      )}
                      Get Suggestions
                    </Button>
                  </div>

                  {aiSuggestions && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="space-y-4"
                    >
                      {aiSuggestions.color_suggestions?.length > 0 && (
                        <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-100">
                          <div className="flex items-center gap-2 mb-3">
                            <Palette className="w-4 h-4 text-amber-600" />
                            <h4 className="font-medium text-sm">Color Palettes</h4>
                          </div>
                          <div className="space-y-3">
                            {aiSuggestions.color_suggestions.map((suggestion, i) => (
                              <button
                                key={i}
                                className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-white/50 transition-colors text-left"
                                onClick={() => {
                                  setCustomization(prev => ({
                                    ...prev,
                                    backgroundColor: suggestion.secondary || prev.backgroundColor,
                                    accentColor: suggestion.primary,
                                  }));
                                  toast.success("Colors applied!");
                                }}
                              >
                                <div className="flex gap-1">
                                  <div className="w-5 h-5 rounded" style={{ backgroundColor: suggestion.primary }} />
                                  <div className="w-5 h-5 rounded" style={{ backgroundColor: suggestion.secondary }} />
                                  {suggestion.accent && (
                                    <div className="w-5 h-5 rounded" style={{ backgroundColor: suggestion.accent }} />
                                  )}
                                </div>
                                <span className="text-xs text-muted-foreground flex-1">{suggestion.reason}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {aiSuggestions.font_suggestions?.length > 0 && (
                        <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-100">
                          <div className="flex items-center gap-2 mb-3">
                            <Type className="w-4 h-4 text-amber-600" />
                            <h4 className="font-medium text-sm">Font Pairings</h4>
                          </div>
                          <div className="space-y-2">
                            {aiSuggestions.font_suggestions.map((suggestion, i) => (
                              <div key={i} className="text-sm">
                                <span className="font-medium">{suggestion.heading} + {suggestion.body}</span>
                                <p className="text-xs text-muted-foreground">{suggestion.reason}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {aiSuggestions.layout_tips?.length > 0 && (
                        <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-100">
                          <div className="flex items-center gap-2 mb-3">
                            <LayoutGrid className="w-4 h-4 text-amber-600" />
                            <h4 className="font-medium text-sm">Layout Tips</h4>
                          </div>
                          <ul className="space-y-1">
                            {aiSuggestions.layout_tips.map((tip, i) => (
                              <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                                <span className="text-amber-500">•</span>
                                {tip}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>
              </TabsContent>
            </ScrollArea>
          </Tabs>
        </aside>

        {/* Main Canvas */}
        <main className="flex-1 bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-8 overflow-auto">
          <div className="relative">
            {/* Card Preview */}
            <motion.div
              ref={cardRef}
              className={`card-preview w-[420px] overflow-hidden ${getShadowClass()}`}
              style={{
                backgroundColor: customization.backgroundColor,
                fontFamily: customization.fontFamily,
                borderRadius: customization.borderRadius,
              }}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
              data-testid="card-preview"
            >
              <div
                className="w-full h-full p-8 flex flex-col justify-between"
                style={{ textAlign: customization.textAlign }}
              >
                {/* Top Section */}
                <div className={`flex items-start ${customization.textAlign === 'center' ? 'flex-col items-center' : customization.textAlign === 'right' ? 'flex-row-reverse' : ''} justify-between`}>
                  {/* Logo */}
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden transition-all duration-300"
                    style={{
                      backgroundColor: cardData.logo_url ? "transparent" : customization.accentColor,
                      borderRadius: customization.borderRadius / 2,
                    }}
                  >
                    {cardData.logo_url ? (
                      <img src={cardData.logo_url} alt="Logo" className="w-full h-full object-contain" />
                    ) : (
                      <span
                        style={{ color: customization.backgroundColor }}
                        className="text-lg font-bold"
                      >
                        {cardData.company_name?.charAt(0) || "C"}
                      </span>
                    )}
                  </div>

                  {/* Job Title */}
                  {customization.textAlign !== 'center' && (
                    <span
                      className="text-xs uppercase tracking-wider"
                      style={{ color: getTextColorWithOpacity(0.5) }}
                    >
                      {cardData.job_title || "Job Title"}
                    </span>
                  )}
                </div>

                {/* Bottom Section */}
                <div>
                  {customization.textAlign === 'center' && cardData.job_title && (
                    <span
                      className="text-xs uppercase tracking-wider block mb-2"
                      style={{ color: getTextColorWithOpacity(0.5) }}
                    >
                      {cardData.job_title}
                    </span>
                  )}

                  {/* Name */}
                  <h2
                    className="text-2xl font-bold mb-1"
                    style={{ color: customization.textColor, fontFamily: "'Playfair Display', serif" }}
                  >
                    {cardData.full_name || "Your Name"}
                  </h2>

                  {/* Company */}
                  {cardData.company_name && (
                    <p className="text-sm mb-3" style={{ color: getTextColorWithOpacity(0.6) }}>
                      {cardData.company_name}
                    </p>
                  )}

                  {/* Contact Details */}
                  <div className="space-y-0.5">
                    {cardData.email && (
                      <p className="text-sm" style={{ color: getTextColorWithOpacity(0.7) }}>
                        {cardData.email}
                      </p>
                    )}
                    {cardData.phone && (
                      <p className="text-sm" style={{ color: getTextColorWithOpacity(0.7) }}>
                        {cardData.phone}
                      </p>
                    )}
                    {cardData.website && (
                      <p className="text-sm" style={{ color: getTextColorWithOpacity(0.7) }}>
                        {cardData.website}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Print-safe indicator */}
            <div className="absolute -bottom-10 left-0 right-0 text-center">
              <span className="text-xs text-muted-foreground bg-white/80 px-3 py-1 rounded-full">
                Standard size: 3.5" × 2" • Print-ready
              </span>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Editor;
